import React, { useEffect, useState } from 'react';
import { LocaleSwitcher } from '../../i18n/components/LocaleSwitcher';

interface AuthenticationUiLayoutProps {
  leftContent?: React.ReactNode;
  children: React.ReactNode;
}

interface NodeState {
  id: string;
  title: string;
  category: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  content: React.ReactNode;
}

/* React Flow Interactive Draggable Hiring Process Canvas (Top-to-Bottom Flow) */
const ReactFlowHiringCanvas: React.FC = () => {
  // Initial Nodes Stacked Top-to-Bottom on Left Canvas
  const [nodes, setNodes] = useState<NodeState[]>([
    {
      id: 'node-1',
      title: '1. Job Opening Criteria',
      category: 'Input Node',
      x: 35,
      y: 40,
      width: 250,
      height: 100,
      color: '#059669',
      content: (
        <div className='space-y-1.5'>
          <div className='flex items-center gap-2'>
            <div className='w-5 h-5 rounded bg-emerald-600 flex items-center justify-center text-[10px] text-white font-bold shadow-sm'>
              #
            </div>
            <span className='text-xs font-bold text-slate-800'>Senior Full-Stack Engineer</span>
          </div>
          <div className='flex items-center gap-2 text-[10px] text-slate-500 font-medium'>
            <span className='bg-slate-100 px-1.5 py-0.5 rounded'>React/TS</span>
            <span className='bg-slate-100 px-1.5 py-0.5 rounded'>Node.js</span>
            <span className='text-emerald-600 font-bold'>Active</span>
          </div>
        </div>
      ),
    },
    {
      id: 'node-2',
      title: '2. AI Skill Assessment',
      category: 'Process Node',
      x: 35,
      y: 185,
      width: 250,
      height: 100,
      color: '#10b981',
      content: (
        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <span className='text-xs text-slate-600 font-medium'>AI Score Threshold</span>
            <span className='text-xs font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-lg'>
              94.8%
            </span>
          </div>
          <div className='w-full h-1.5 bg-slate-100 rounded-full overflow-hidden'>
            <div className='h-full bg-emerald-500 rounded-full w-[94.8%]' />
          </div>
        </div>
      ),
    },
    {
      id: 'node-3',
      title: '3. Candidate Evaluation',
      category: 'Evaluation Node',
      x: 35,
      y: 330,
      width: 250,
      height: 100,
      color: '#047857',
      content: (
        <div className='flex items-center justify-between'>
          <div>
            <span className='text-xs font-bold text-slate-800 block'>Top Candidates</span>
            <span className='text-[10px] text-slate-500'>Verified Skill Profiles</span>
          </div>
          <span className='text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-2.5 py-1 rounded-lg'>
            14 Selected
          </span>
        </div>
      ),
    },
    {
      id: 'node-4',
      title: '4. Hiring Output',
      category: 'Output Node',
      x: 35,
      y: 475,
      width: 250,
      height: 145,
      color: '#059669',
      content: (
        <div className='space-y-2'>
          <div className='flex items-center justify-between text-xs font-semibold text-slate-700'>
            <span>Offer Issued</span>
            <span className='text-[10px] text-emerald-600 font-bold bg-emerald-50 px-1.5 py-0.5 rounded'>Hired</span>
          </div>
          <div className='h-16 w-full bg-slate-50 rounded-xl border border-slate-200/80 flex items-center justify-center relative overflow-hidden'>
            <div className='absolute inset-0 flex items-center justify-center gap-1.5 opacity-90 animate-pulse'>
              <div className='w-5 h-5 rounded-md bg-emerald-600 transform rotate-12 shadow-sm' />
              <div className='w-7 h-7 rounded-md bg-[#047857] transform -rotate-6 shadow-md' />
              <div className='w-4 h-4 rounded-md bg-teal-600 transform rotate-45 shadow-sm' />
              <div className='w-6 h-6 rounded-md bg-emerald-700 transform -rotate-12 shadow-md' />
            </div>
          </div>
        </div>
      ),
    },
  ]);

  // Dragging State
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const node = nodes.find((n) => n.id === id);
    if (!node) return;
    setDraggingId(id);
    setDragOffset({
      x: e.clientX - node.x,
      y: e.clientY - node.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!draggingId) return;
    setNodes((prev) =>
      prev.map((n) => {
        if (n.id === draggingId) {
          return {
            ...n,
            x: Math.max(10, Math.min(window.innerWidth - n.width - 10, e.clientX - dragOffset.x)),
            y: Math.max(10, Math.min(window.innerHeight - n.height - 10, e.clientY - dragOffset.y)),
          };
        }
        return n;
      })
    );
  };

  const handleMouseUp = () => {
    setDraggingId(null);
  };

  const n1 = nodes.find((n) => n.id === 'node-1') || nodes[0];
  const n2 = nodes.find((n) => n.id === 'node-2') || nodes[1];
  const n3 = nodes.find((n) => n.id === 'node-3') || nodes[2];
  const n4 = nodes.find((n) => n.id === 'node-4') || nodes[3];

  // Calculate Handles for Real-time Top-to-Bottom Sequential SVG Bezier Routing
  const p1Bottom = { x: n1.x + n1.width / 2, y: n1.y + n1.height };
  const p2Top = { x: n2.x + n2.width / 2, y: n2.y };
  const p2Bottom = { x: n2.x + n2.width / 2, y: n2.y + n2.height };
  const p3Top = { x: n3.x + n3.width / 2, y: n3.y };
  const p3Bottom = { x: n3.x + n3.width / 2, y: n3.y + n3.height };
  const p4Top = { x: n4.x + n4.width / 2, y: n4.y };

  // Smooth Top-to-Bottom Bezier Curves
  const path1 = `M ${p1Bottom.x} ${p1Bottom.y} C ${p1Bottom.x} ${p1Bottom.y + 25}, ${p2Top.x} ${p2Top.y - 25}, ${p2Top.x} ${p2Top.y}`;
  const path2 = `M ${p2Bottom.x} ${p2Bottom.y} C ${p2Bottom.x} ${p2Bottom.y + 25}, ${p3Top.x} ${p3Top.y - 25}, ${p3Top.x} ${p3Top.y}`;
  const path3 = `M ${p3Bottom.x} ${p3Bottom.y} C ${p3Bottom.x} ${p3Bottom.y + 25}, ${p4Top.x} ${p4Top.y - 25}, ${p4Top.x} ${p4Top.y}`;

  return (
    <div
      className='absolute inset-0 overflow-hidden select-none z-0'
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* 1. Dot Matrix Canvas Grid */}
      <div
        className='absolute inset-0 opacity-[0.45] pointer-events-none'
        style={{
          backgroundImage: 'radial-gradient(#64748b 1.5px, transparent 1.5px)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* 2. Soft Glowing Ambient Aura Blobs */}
      <div className='absolute -top-20 left-10 w-[550px] h-[550px] bg-emerald-500/15 rounded-full blur-[140px] pointer-events-none animate-pulse' />
      <div className='absolute bottom-10 left-10 w-[600px] h-[600px] bg-[#047857]/15 rounded-full blur-[160px] pointer-events-none' />

      {/* 3. SVG Bezier Connecting Paths directly connecting Top-to-Bottom Draggable Handles */}
      <svg className='absolute inset-0 w-full h-full fill-none pointer-events-none z-0'>
        <defs>
          <style>{`
            @keyframes reactFlowDash {
              from { stroke-dashoffset: 0; }
              to { stroke-dashoffset: -240; }
            }
            .animate-flow-line {
              stroke-dasharray: 6 6;
              animation: reactFlowDash 12s linear infinite;
            }
            .animate-flow-line-fast {
              stroke-dasharray: 6 6;
              animation: reactFlowDash 8s linear infinite;
            }
          `}</style>
        </defs>

        {/* Path 1: Job Criteria ──► Skill Assessment */}
        <path d={path1} stroke='#059669' strokeWidth='2.5' strokeOpacity='0.9' className='animate-flow-line' />

        {/* Path 2: Skill Assessment ──► Candidate Pool */}
        <path d={path2} stroke='#10b981' strokeWidth='2.5' strokeOpacity='0.9' className='animate-flow-line-fast' />

        {/* Path 3: Candidate Pool ──► Hiring Output */}
        <path d={path3} stroke='#047857' strokeWidth='2.5' strokeOpacity='0.9' className='animate-flow-line' />
      </svg>

      {/* 4. Interactive Draggable React Flow Node Cards */}
      {nodes.map((node) => (
        <div
          key={node.id}
          onMouseDown={(e) => handleMouseDown(e, node.id)}
          style={{
            left: `${node.x}px`,
            top: `${node.y}px`,
            width: `${node.width}px`,
          }}
          className={`hidden lg:flex absolute flex-col bg-white/95 backdrop-blur-md p-3.5 rounded-2xl border border-slate-200 shadow-xl opacity-95 transition-shadow duration-200 cursor-grab active:cursor-grabbing z-20 hover:shadow-2xl hover:scale-[1.02] ${
            draggingId === node.id ? 'ring-2 ring-emerald-500 shadow-2xl scale-105 z-30' : ''
          }`}
        >
          {/* Top Header */}
          <div className='flex items-center justify-between border-b border-slate-100 pb-1.5 mb-2 pointer-events-none'>
            <span className='text-[10px] font-bold text-slate-500 uppercase tracking-wider'>
              {node.title}
            </span>
            <span className='w-2 h-2 rounded-full bg-emerald-500 animate-ping' />
          </div>

          {/* Node Content */}
          <div className='pointer-events-none'>{node.content}</div>

          {/* Top / Bottom Connecting Handle Dots */}
          {node.id !== 'node-1' && (
            <div className='absolute left-1/2 -top-2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-white border-2 border-emerald-600 shadow-[0_0_8px_rgba(5,150,105,0.5)] z-30' />
          )}

          {node.id !== 'node-4' && (
            <div className='absolute left-1/2 -bottom-2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-white border-2 border-emerald-600 shadow-[0_0_8px_rgba(5,150,105,0.5)] z-30' />
          )}
        </div>
      ))}
    </div>
  );
};

/* AI HireTalent Showcase Panel for Left Hero Column (Replacing Pitch Black) */
export const AiTalentShowcase: React.FC = () => (
  <div className='w-full h-full flex flex-col justify-between p-6 sm:p-8 text-white relative z-10 select-none'>
    {/* Top Header Badge & Tagline */}
    <div className='space-y-3'>
      <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-semibold backdrop-blur-md'>
        <span className='w-2 h-2 rounded-full bg-emerald-400 animate-pulse' />
        <span>AI Talent Intelligence</span>
      </div>
      <h2 className='text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight text-white'>
        Smart Hiring. <br />
        <span className='text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-emerald-200'>
          Evaluated with AI.
        </span>
      </h2>
      <p className='text-xs text-slate-300 font-normal leading-relaxed max-w-xs'>
        Discover, assess, and hire top 1% tech candidates seamlessly with automated evaluation pipelines.
      </p>
    </div>

    {/* Center Candidate Intelligence Showcase Card */}
    <div className='my-6 p-4 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/15 shadow-2xl relative overflow-hidden group hover:border-emerald-500/40 transition-all duration-300'>
      {/* Background glow */}
      <div className='absolute -right-10 -bottom-10 w-32 h-32 bg-emerald-500/20 rounded-full blur-2xl group-hover:bg-emerald-500/30 transition-all' />

      <div className='flex items-center justify-between mb-3 border-b border-white/10 pb-2.5'>
        <div className='flex items-center gap-2.5'>
          <div className='w-8 h-8 rounded-full bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center text-white font-bold text-xs shadow-md'>
            AR
          </div>
          <div>
            <h4 className='text-xs font-bold text-white leading-none'>Alex Rivera</h4>
            <span className='text-[10px] text-emerald-300/80 font-medium'>Senior Systems Architect</span>
          </div>
        </div>
        <div className='text-right'>
          <span className='text-xs font-extrabold text-emerald-400 bg-emerald-500/20 px-2 py-0.5 rounded-lg border border-emerald-500/30'>
            98.4% Match
          </span>
        </div>
      </div>

      {/* Skill Assessment Breakdown Progress Bars */}
      <div className='space-y-2 text-[11px]'>
        <div>
          <div className='flex justify-between text-slate-200 text-[10px] font-medium mb-1'>
            <span>System Architecture</span>
            <span className='text-emerald-300 font-bold'>99%</span>
          </div>
          <div className='w-full h-1.5 bg-slate-800/80 rounded-full overflow-hidden'>
            <div className='h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full w-[99%]' />
          </div>
        </div>

        <div>
          <div className='flex justify-between text-slate-200 text-[10px] font-medium mb-1'>
            <span>Coding & Algorithms</span>
            <span className='text-emerald-300 font-bold'>96%</span>
          </div>
          <div className='w-full h-1.5 bg-slate-800/80 rounded-full overflow-hidden'>
            <div className='h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full w-[96%]' />
          </div>
        </div>
      </div>
    </div>

    {/* Bottom Live Metrics Row */}
    <div className='flex items-center justify-between border-t border-white/10 pt-4 text-[11px] text-slate-300'>
      <div>
        <div className='text-base font-extrabold text-white'>2,490+</div>
        <div className='text-[10px] text-slate-400'>Evaluated</div>
      </div>
      <div className='h-6 w-px bg-white/15' />
      <div>
        <div className='text-base font-extrabold text-emerald-400'>99.2%</div>
        <div className='text-[10px] text-slate-400'>Precision</div>
      </div>
      <div className='h-6 w-px bg-white/15' />
      <div className='flex items-center gap-1.5 text-xs text-emerald-300 font-medium bg-emerald-950/60 px-2.5 py-1 rounded-full border border-emerald-500/20'>
        <span className='w-2 h-2 rounded-full bg-emerald-400 animate-ping' />
        <span>Engine Active</span>
      </div>
    </div>
  </div>
);

export const AuthenticationUiLayout: React.FC<AuthenticationUiLayoutProps> = ({
  leftContent,
  children,
}) => {
  return (
    <div className='min-h-screen w-full flex items-center justify-center p-4 sm:p-6 md:p-10 bg-slate-50 relative overflow-hidden font-sans'>
      {/* Top right language switcher */}
      <div className='absolute top-5 right-6 z-30'>
        <LocaleSwitcher variant='inline' />
      </div>

      {/* Interactive React Flow Hiring Process Canvas Background */}
      <ReactFlowHiringCanvas />

      {/* Main Container Card (Split 2-Column Layout) */}
      <div className='relative z-10 w-full max-w-[1020px] min-h-[580px] my-auto flex flex-col md:flex-row rounded-[2.25rem] bg-white shadow-[0_25px_70px_-15px_rgba(0,0,0,0.14)] border border-slate-200/90 overflow-hidden transition-all duration-300 pointer-events-auto'>
        {/* Left Column: AI HireTalent Showcase Section (Replacing pitch black) */}
        <div className='w-full md:w-1/2 bg-gradient-to-br from-[#051c14] via-[#08291d] to-[#03130d] min-h-[300px] md:min-h-[580px] flex flex-col items-center justify-between relative select-none overflow-hidden border-r border-emerald-900/40'>
          {leftContent || <AiTalentShowcase />}
        </div>

        {/* Right Column: Clean White Form Section */}
        <div className='w-full md:w-1/2 bg-white p-8 sm:p-12 md:p-14 flex items-center justify-center relative'>
          <div className='w-full max-w-sm mx-auto'>{children}</div>
        </div>
      </div>
    </div>
  );
};





